import { useState, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSalesRepData } from "@/hooks/useSalesRepData";
import { useAppData } from "@/data/AppDataContext";
import { isClosedStatus } from "@/data/quoteTypes";
import { getJobDisplayValue } from "@/data/jobPricingSelectors";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/StatusBadge";
import { RoleContextBanner } from "@/components/RoleContextBanner";
import { RoleHandoffInbox } from "@/components/RoleHandoffInbox";
import { JobActivityTimeline } from "@/components/JobActivityTimeline";
import { AppointmentFlow } from "@/components/AppointmentFlow";
import { NoDemoDispositionForm } from "@/components/NoDemoDispositionForm";
import { ResultAppointmentDialog, type ResultAppointmentData } from "@/components/ResultAppointmentDialog";
import { formatAddress } from "@/lib/addressUtils";
import { REVISION_REASON_LABELS, type RevisionReason, type BuildReviewEvent } from "@/data/buildReviewTypes";
import { EXECUTION_METHOD_LABELS, PRIORITY_CONFIG, TOUCH_OUTCOME_LABELS, type TouchOutcome, getConfigDrivenDefaults, getFollowUpDefaults, calculateDueDate, getThankYouMessage } from "@/data/unifiedFollowUp";
import { useFollowUpEngine } from "@/data/FollowUpEngineContext";
import { deriveNextStep, deriveLifecycle } from "@/data/situational/outcomeCapture";
import { getLeadSourceRescheduleRisk } from "@/data/leadSourceRisk";
import { differenceInHours, format, isToday, isFuture, isPast, parseISO, addBusinessDays } from "date-fns";
import { toast } from "sonner";
import { findRepAppointmentOverlap } from "@/lib/appointmentOverlap";
import { isUuid } from "@/lib/uuid";
import {
  AlertTriangle, ArrowRight, Clock, Eye, CheckCircle,
  Camera, Ruler, Package, MessageSquare, Wrench,
  CalendarDays, Briefcase, Receipt, DollarSign, User,
  TrendingUp, MapPin, X, RotateCcw, FileText, Send,
  Bell, Mail, Play, Phone, Lock, CalendarCheck,
  Copy, PhoneCall, MessageCircle, ChevronDown, ChevronUp,
} from "lucide-react";
import type { Job } from "@/data/types";
import type { Lead, NoDemoDisposition, FollowUpTask, DispositionReason } from "@/data/pipelineTypes";
import { getMessagesNeedingAttention, createJobMessage, markMessageResponded, deriveJobParticipants, type JobMessage } from "@/data/jobMessageTypes";
import { MyScheduleWidget } from "@/components/MyScheduleWidget";
import { useUISkin } from "@/data/UISkinContext";
import { CockpitHeader, StatStrip, type StatItem } from "@/components/dashboard/primitives";

// ── SLA helpers ──
type SlaLevel = "normal" | "warning" | "overdue";

function getSlaLevel(hoursWaiting: number): SlaLevel {
  if (hoursWaiting >= 12) return "overdue";
  if (hoursWaiting >= 4) return "warning";
  return "normal";
}

const SLA_CONFIG: Record<SlaLevel, { label: string; color: string; bg: string; borderColor: string; dotColor: string }> = {
  normal:  { label: "Normal",  color: "text-success",      bg: "bg-success/10",      borderColor: "border-success/20",      dotColor: "bg-success" },
  warning: { label: "Warning", color: "text-warning",      bg: "bg-warning/10",      borderColor: "border-warning/20",      dotColor: "bg-warning" },
  overdue: { label: "Overdue", color: "text-destructive",  bg: "bg-destructive/10",  borderColor: "border-destructive/20",  dotColor: "bg-destructive" },
};

function formatWaitTime(hours: number): string {
  if (hours < 1) return "< 1h";
  if (hours < 24) return `${Math.floor(hours)}h`;
  const days = Math.floor(hours / 24);
  const rem = Math.floor(hours % 24);
  return rem > 0 ? `${days}d ${rem}h` : `${days}d`;
}

function getProductLabel(job: Job): string {
  const material = job.projectConfig?.bathroomMaterials?.[0] ?? job.projectConfig?.primaryMaterial ?? "";
  const pt = job.projectType === "Tub to Shower Conversion" ? "Shower Conversion" : job.projectType;
  return [material, pt].filter(Boolean).join(" ");
}

// ── Appointment access control — re-exported from unified selectors ──
import {
  type AppointmentStatus as UnifiedApptStatus,
  type UnifiedAppointment,
  getAllAppointments,
  getCommandCenterAppointments,
} from "@/data/appointmentSelectors";

type AppointmentStatus = UnifiedApptStatus;

const APPT_STATUS_CONFIG: Record<AppointmentStatus, { label: string; color: string; bg: string; dot: string }> = {
  scheduled:    { label: "Upcoming",    color: "text-primary",      bg: "bg-primary/10 border-primary/20",       dot: "bg-primary" },
  in_progress: { label: "In Progress", color: "text-warning",      bg: "bg-warning/10 border-warning/20",       dot: "bg-warning" },
  completed:   { label: "Completed",   color: "text-success",      bg: "bg-success/10 border-success/20",       dot: "bg-success" },
  locked:      { label: "Locked",      color: "text-muted-foreground", bg: "bg-muted/30 border-border",          dot: "bg-muted-foreground" },
  no_show:     { label: "No Show",     color: "text-destructive",  bg: "bg-destructive/10 border-destructive/20", dot: "bg-destructive" },
  rescheduled: { label: "Rescheduled", color: "text-warning",      bg: "bg-warning/10 border-warning/20",       dot: "bg-warning" },
};

interface HoldJob {
  job: Job;
  revisionEvent: BuildReviewEvent | undefined;
  hoursWaiting: number;
  sla: SlaLevel;
}

export function SalesCommandCenter() {
  const data = useSalesRepData();
  const navigate = useNavigate();
  const { buildReviewEvents, setBuildReviewEvents, jobs, setJobs, jobMessages, setJobMessages, leads, setLeads, followUpTasks, setFollowUpTasks, messages, setMessages, appointmentResults, setAppointmentResults, addAppointmentOutcomeExtension, salesOpportunities, conversations, consultationSessions } = useAppData();
  const { config: engineConfig } = useFollowUpEngine();
  const { skin } = useUISkin();
  const isCockpit = skin === "cockpit";
  const [resolvingJobId, setResolvingJobId] = useState<string | null>(null);
  const [clarificationNote, setClarificationNote] = useState("");
  const [respondingMessageId, setRespondingMessageId] = useState<string | null>(null);
  const [responseText, setResponseText] = useState("");
  const [activeAppointment, setActiveAppointment] = useState<Lead | null>(null);
  const [dispositionLead, setDispositionLead] = useState<Lead | null>(null);
  const [resultLead, setResultLead] = useState<Lead | null>(null);
  const [expandedFollowUp, setExpandedFollowUp] = useState<string | null>(null);

  const now = new Date();

   // ── Unified appointment dataset (single source of truth) ──
   const allUnified = useMemo(() =>
     getAllAppointments({ salesOpportunities, conversations, messages, followUpTasks, consultationSessions }),
     [salesOpportunities, conversations, messages, followUpTasks, consultationSessions],
   );

   const appointments = useMemo(() => {
     const repName = data.repName;
     return getCommandCenterAppointments(allUnified, repName)
       .map(u => ({ 
         lead: u.opportunity, 
         status: u.accessStatus, 
         locked: u.locked, 
         unlockLabel: u.unlockLabel,
         hasConsultationSession: u.hasConsultationSession,
       }));
   }, [allUnified, data.repName]);

  // ── Lead-source reschedule risk (Phase B v1) ──
  // Memoized per render: computed once across all jobs+opportunities, then
  // looked up per appointment card by source string. Returns null when the
  // source doesn't pass the triple gate (N≥3, ≥30%, ≥1.5× platform rate).
  const riskBySource = useMemo(() => {
    const cache = new Map<string, ReturnType<typeof getLeadSourceRescheduleRisk>>();
    const sources = new Set<string>();
    for (const { lead } of appointments) {
      if (lead.leadSource) sources.add(lead.leadSource);
    }
    for (const src of sources) {
      cache.set(src, getLeadSourceRescheduleRisk(src, jobs, salesOpportunities));
    }
    return cache;
  }, [appointments, jobs, salesOpportunities]);

  // Jobs on hold requiring sales action
  const jobsOnHold = useMemo((): HoldJob[] => {
    return data.myJobs
      .filter(j => {
        const evts = buildReviewEvents.filter(e => e.jobId === j.id);
        if (evts.length === 0) return false;
        return evts[evts.length - 1].action === "revision_requested";
      })
      .map(j => {
        const evts = buildReviewEvents.filter(e => e.jobId === j.id);
        const revEvt = evts.filter(e => e.action === "revision_requested").pop();
        const hoursWaiting = revEvt ? differenceInHours(now, new Date(revEvt.timestamp)) : 0;
        return { job: j, revisionEvent: revEvt, hoursWaiting, sla: getSlaLevel(hoursWaiting) };
      })
      .sort((a, b) => b.hoursWaiting - a.hoursWaiting);
  }, [data.myJobs, buildReviewEvents, now]);

  const activeJobs = data.myJobs.filter(j => j.currentStage !== "Completed");
  const activeQuotes = data.myQuotes.filter(q => !isClosedStatus(q.stage));

  // ── Follow-ups for this rep ──
  const myFollowUps = useMemo(() => {
    const repName = data.repName;
    return followUpTasks
      .filter(t => (t.type === "rep" && t.assignedTo === repName) || t.type === "manager")
      .filter(t => t.status === "pending")
      .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime());
  }, [followUpTasks, data.repName]);

  // ── Needs Attention: unread messages ──
  const needsAttention = useMemo(() => {
    const salesRepName = data.myJobs[0]?.salesRep || "Mike";
    const salesRepIds = [salesRepName, "sales-rep", "sales-manager"];
    const unreadMessages: { message: JobMessage; job: Job }[] = [];
    jobMessages.forEach((msg) => {
      if (msg.senderRole === "sales") return;
      const hasUnread = salesRepIds.some((id) => msg.recipientStatuses[id] === "unread");
      if (!hasUnread) return;
      const job = jobs.find((j) => j.id === msg.jobId);
      if (job) unreadMessages.push({ message: msg, job });
    });
    return unreadMessages.sort(
      (a, b) => new Date(b.message.timestamp).getTime() - new Date(a.message.timestamp).getTime()
    );
  }, [jobMessages, jobs, data.myJobs]);

  const handleResubmitForReview = (jobId: string) => {
    const evt: BuildReviewEvent = {
      id: crypto.randomUUID(),
      jobId,
      action: "sales_resubmitted",
      timestamp: new Date().toISOString(),
      performedBy: "sales",
      notes: clarificationNote.trim() || "Sales resubmitted job for build review",
    };
    setBuildReviewEvents(prev => [...prev, evt]);
    setJobs(prev => prev.map(j =>
      j.id === jobId ? { ...j, holdStatus: undefined, holdReason: undefined } : j
    ));
    setClarificationNote("");
    setResolvingJobId(null);
    toast.success("Job resubmitted for review", { description: "Moved back to Intake Review queue" });
  };

  const handleRespondToMessage = useCallback((messageId: string) => {
    if (!responseText.trim()) return;
    const original = jobMessages.find((m) => m.id === messageId);
    if (!original) return;
    const job = jobs.find((j) => j.id === original.jobId);
    if (!job) return;

    setJobMessages((prev) =>
      prev.map((m) => {
        if (m.id !== messageId) return m;
        const updated = { ...m, recipientStatuses: { ...m.recipientStatuses } };
        Object.keys(updated.recipientStatuses).forEach((k) => {
          if (updated.recipientStatuses[k] === "unread") updated.recipientStatuses[k] = "responded";
        });
        return updated;
      })
    );

    const participants = deriveJobParticipants(job);
    const responseMsg = createJobMessage(
      original.jobId,
      responseText.trim(),
      { id: job.salesRep || "Sales Rep", name: job.salesRep || "Sales Rep", role: "sales" },
      [original.senderId, participants.reviewerId || "reviewer"].filter(Boolean),
      { context: "Build Review", priority: "normal" },
    );
    setJobMessages((prev) => [...prev, responseMsg]);
    setRespondingMessageId(null);
    setResponseText("");
    toast.success("Response sent");
  }, [responseText, jobMessages, jobs, setJobMessages]);

  const handleDismissMessage = useCallback((messageId: string) => {
    setJobMessages((prev) =>
      prev.map((m) => {
        if (m.id !== messageId) return m;
        const updated = { ...m, recipientStatuses: { ...m.recipientStatuses } };
        Object.keys(updated.recipientStatuses).forEach((k) => {
          if (updated.recipientStatuses[k] === "unread") updated.recipientStatuses[k] = "read";
        });
        return updated;
      })
    );
  }, [setJobMessages]);

  // ── No-Demo Disposition handler ──
  const handleDisposition = (lead: Lead, disposition: NoDemoDisposition) => {
    const reasonLabel = disposition.reason.replace(/_/g, " ");
    const defaults = getConfigDrivenDefaults(reasonLabel, lead.primaryName, lead.assignedRep, engineConfig);

    // Update lead with disposition and move to Closed Lost
    setLeads(prev => prev.map(l =>
      l.id === lead.id ? {
        ...l,
        disposition,
        stage: "Closed Lost" as const,
        stageHistory: [...l.stageHistory, {
          id: crypto.randomUUID(),
          fromStage: l.stage,
          toStage: "Closed Lost" as const,
          changedAt: new Date().toISOString(),
          changedBy: lead.assignedRep,
        }],
        updatedAt: new Date().toISOString(),
      } : l
    ));

    // Create follow-up task based on type with intelligent defaults
    if (disposition.followUpType === "rep" || disposition.followUpType === "manager") {
      const dueDate = disposition.followUpDateTime || calculateDueDate(defaults.dueDays);
      const task: FollowUpTask = {
        id: crypto.randomUUID(),
        opportunityId: lead.id,
        type: disposition.followUpType,
        assignedTo: disposition.followUpType === "manager" ? "Manager" : lead.assignedRep,
        dueDate,
        status: "pending",
        notes: disposition.followUpNotes,
        createdAt: new Date().toISOString(),
        disposition,
        customerName: lead.primaryName,
        customerPhone: lead.primaryPhone,
        dispositionReason: reasonLabel,
        executionMethod: defaults.method,
        priority: defaults.priority,
        suggestedScript: defaults.script,
        sequence: defaults.sequence,
        thankYouSent: true,
        thankYouSentAt: new Date().toISOString(),
      };
      setFollowUpTasks(prev => [...prev, task]);

      // Schedule auto thank-you SMS using config-driven delay
      const thankYouMsg = defaults.thankYouMessage || getThankYouMessage(lead.primaryName);
      const thankYouDelay = defaults.thankYouDelayMinutes || 30;
      const thankYouMessage = {
        id: `msg-ty-${Date.now()}`,
        conversationId: lead.id,
        direction: "outbound" as const,
        channel: "sms" as const,
        body: thankYouMsg,
        sender: lead.assignedRep,
        timestamp: new Date(Date.now() + thankYouDelay * 60 * 1000).toISOString(),
        deliveryStatus: "pending" as const,
        tag: "auto-thank-you",
      };
      setMessages(prev => [...prev, thankYouMessage]);

      if (disposition.followUpType === "manager") {
        toast.success("Manager follow-up created", { description: `Due ${format(new Date(dueDate), "MMM d")} — GM, Sales Manager, and Admin notified` });
      } else {
        toast.success("Follow-up task created", { description: `Due ${format(new Date(dueDate), "MMM d, h:mm a")}` });
      }
      toast.info("Thank-you SMS scheduled", { description: "Automatic message will send in 30 minutes." });
    } else {
      toast.info("Appointment closed", { description: "No follow-up required" });
    }

    setDispositionLead(null);
  };

  // ── Result Appointment handler ──
  const handleResultAppointment = (lead: Lead, data: ResultAppointmentData) => {
    const now = new Date().toISOString();
    const resultId = crypto.randomUUID();

    // Determine next action label
    const getNextAction = (outcome: string, reason?: string): string => {
      if (outcome === "sold") return "Job creation and financial process";
      if (outcome === "no_sale") return "Follow-up task assigned";
      if (outcome === "rescheduled") return "New appointment scheduled";
      if (outcome === "no_demo" && reason === "rescheduled") return "Reschedule appointment created";
      return "Recovery follow-up assigned";
    };

    // Build immutable result record
    const resultRecord = {
      id: resultId,
      appointmentId: lead.id,
      leadId: lead.id,
      opportunityId: lead.id,
      customerName: lead.primaryName || "Unknown",
      assignedRep: lead.assignedRep || "Unassigned",
      source: lead.leadSource || "",
      appointmentDatetime: lead.appointmentTime || now,
      resultType: data.outcome as "sold" | "no_sale" | "rescheduled" | "no_demo",
      noSaleReason: data.outcome === "no_sale" ? data.reason : undefined,
      noDemoReason: data.outcome === "no_demo" ? data.reason : undefined,
      rescheduleDatetime: data.rescheduleDate && data.rescheduleTime ? `${data.rescheduleDate}T${data.rescheduleTime}:00` : undefined,
      buildSummary: data.pricingSnapshot?.productSummary,
      pricingSnapshot: data.pricingSnapshot ? {
        standardPrice: data.pricingSnapshot.standardPrice,
        promoPrice: data.pricingSnapshot.promoPrice,
        bestValuePrice: data.pricingSnapshot.bestValuePrice,
        paymentMethod: data.pricingSnapshot.paymentMethod,
        productSummary: data.pricingSnapshot.productSummary,
      } : undefined,
      notes: data.notes,
      submittedAt: now,
      submittedBy: lead.assignedRep || "system",
      nextAction: getNextAction(data.outcome, data.reason),
      nextActionStatus: "pending" as const,
    };

    // Persist immutable result record
    setAppointmentResults(prev => [...prev, resultRecord]);

    // ── Write structured AppointmentOutcomeExtension (Phase 1 sidecar) ──
    // Hard-immutable: addAppointmentOutcomeExtension throws if a record for
    // this appointmentResultId already exists. Derivations live in the
    // situational layer — never inline branching here.
    try {
      const proposalSnapshot = data.proposalCapture && data.pricingSnapshot ? {
        package: data.proposalCapture.packageTier,
        layout: data.pricingSnapshot.productSummary,
        productSummary: data.pricingSnapshot.productSummary,
        basePrice: data.pricingSnapshot.standardPrice,
        discountAmount: data.proposalCapture.discountAmount,
        discountReason: data.proposalCapture.discountReason,
        finalPrice: Math.max(0, data.pricingSnapshot.standardPrice - data.proposalCapture.discountAmount),
        paymentStructure: (data.paymentMethodConfirmed === "financing" ? "financing"
          : data.paymentMethodConfirmed === "cash" ? "cash"
          : "mixed") as "cash" | "financing" | "mixed",
        financingTerms: data.proposalCapture.financingTerms,
        addOns: [],
      } : undefined;

      const objectionsRecord = data.objectionsCapture
        ? {
            primary: data.objectionsCapture.primary,
            secondary: data.objectionsCapture.secondary,
            notes: data.objectionsCapture.notes,
          }
        : undefined;

      const nextStep = deriveNextStep(
        { resultType: resultRecord.resultType, pricingSnapshot: resultRecord.pricingSnapshot },
        objectionsRecord,
        proposalSnapshot,
      );
      const lifecycle = deriveLifecycle(resultRecord.resultType, objectionsRecord?.primary);

      addAppointmentOutcomeExtension({
        appointmentResultId: resultId,
        proposal: proposalSnapshot,
        objections: objectionsRecord,
        nextStep,
        lifecycle,
        capturedAt: now,
        capturedBy: lead.assignedRep || "system",
      });
    } catch (err) {
      // Defensive: extension write failures must not block the base result.
      // Surface for diagnostics; the base AppointmentResult is already saved.
      console.error("[AppointmentOutcomeExtension] write failed:", err);
    }

    if (data.outcome === "sold") {
      // Auto-create Job from sold appointment
      const jobId = `JOB-${String(jobs.length + 10).padStart(3, "0")}`;
      const contractValue = data.pricingSnapshot?.bestValuePrice || data.pricingSnapshot?.standardPrice || 0;
      const productSummary = data.pricingSnapshot?.productSummary || lead.projectType || "Project";

      const newJob: Job = {
        id: jobId,
        customerName: lead.primaryName,
        address: { street: lead.address, city: lead.city, state: lead.state, zip: lead.zip },
        phone: lead.primaryPhone,
        email: lead.primaryEmail,
        secondaryContactName: lead.secondaryName || "",
        secondaryContactPhone: lead.secondaryPhone || "",
        secondaryContactEmail: lead.secondaryEmail || "",
        projectType: (lead.projectType || "Other") as any,
        assignedCrew: "Unassigned",
        assignmentType: "Unassigned",
        scheduledInstallDate: "",
        permitRequired: false,
        permitStatus: "Not Required",
        orderStatus: "Not Ordered",
        materialsStatus: "Not Ready",
        dispatchStatus: "Not Ready",
        contractorAssignmentStatus: "Unassigned",
        currentStage: "Sale Closed",
        notes: data.notes,
        issueFlag: false,
        scopeSummary: productSummary,
        payoutAmount: contractValue,
        assignedSubcontractor: "",
        targetInstallDate: "",
        activityHistory: [
          { id: crypto.randomUUID(), date: now, action: `Job created from appointment result — ${productSummary}`, user: lead.assignedRep, noteType: "system" },
        ],
        photos: [],
        qaSubmitted: false,
        materialReadiness: "Not Ordered",
        materialReadinessNotes: "",
        missingItemsList: "",
        procurementStatus: "Not Ordered",
        warehouseStatus: "Not Received",
        branch: "Tampa",
        region: "West Florida",
        salesRep: lead.assignedRep,
        closeoutStatus: "Not Started",
        finalDocumentationComplete: false,
        customerSignoff: false,
        closeoutNotes: "",
        rescissionStatus: "In Rescission",
        rescissionEndsAt: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
        scopeLocked: true,
        intakeApproved: false,
        financials: contractValue > 0 ? { contractValue, estimatedMaterialCost: 0, estimatedLaborCost: 0, subcontractorPayout: 0, marginEstimate: contractValue, paymentStatus: "Not Invoiced" as const, payoutStatus: "Not Submitted" as const, invoices: [], payments: [] } : undefined,
        updatedAt: "",
        createdAt: "",
        jobNumber: undefined,
        assignedSubcontractorId: ""
      };
      setJobs(prev => [...prev, newJob]);

      setLeads(prev => prev.map(l =>
        l.id === lead.id ? {
          ...l,
          stage: "Closed Won" as const,
          wonAt: now,
          linkedJobId: jobId,
          stageHistory: [...l.stageHistory, {
            id: crypto.randomUUID(),
            fromStage: l.stage,
            toStage: "Closed Won" as const,
            changedAt: now,
            changedBy: lead.assignedRep,
          }],
          activities: [...(l.activities || []), {
            id: crypto.randomUUID(),
            type: "note" as const,
            description: `Appointment result: SOLD. Payment: ${data.paymentMethodConfirmed || "N/A"}. Job ${jobId} created.`,
            createdAt: now,
            createdBy: lead.assignedRep,
          }],
          updatedAt: now,
        } : l
      ));
      toast.success("Sale recorded — Job created!", {
        description: `${jobId} · ${productSummary}`,
        action: { label: "View Job", onClick: () => navigate(`/jobs/${jobId}`) },
      });

    } else if (data.outcome === "no_sale") {
      setLeads(prev => prev.map(l =>
        l.id === lead.id ? {
          ...l,
          stage: "Follow-Up Needed" as const,
          stageHistory: [...l.stageHistory, {
            id: crypto.randomUUID(),
            fromStage: l.stage,
            toStage: "Follow-Up Needed" as const,
            changedAt: now,
            changedBy: lead.assignedRep,
          }],
          activities: [...(l.activities || []), {
            id: crypto.randomUUID(),
            type: "note" as const,
            description: `Appointment result: NO SALE. Reason: ${data.reason}. Notes: ${data.notes}`,
            createdAt: now,
            createdBy: lead.assignedRep,
          }],
          updatedAt: now,
        } : l
      ));

      // Create follow-up task
      const defaults = getConfigDrivenDefaults(data.reason || "no_sale", lead.primaryName, lead.assignedRep, engineConfig);
      const noSaleDisposition: NoDemoDisposition = {
        reason: "other" as DispositionReason,
        homeownerPresent: true,
        decisionMakerPresent: true,
        mainReasonNoDemoText: data.reason || "no_sale",
        pricingDiscussed: true,
        stillLiveOpportunity: true,
        followUpType: "rep" as const,
        followUpNotes: data.notes,
        bestContactMethod: "in_person",
        completedAt: now,
        completedBy: lead.assignedRep,
      };
      const task: FollowUpTask = {
        disposition: noSaleDisposition,
        id: crypto.randomUUID(),
        opportunityId: lead.id,
        type: "rep" as const,
        assignedTo: lead.assignedRep,
        dueDate: calculateDueDate(defaults.dueDays),
        status: "pending" as const,
        notes: data.notes,
        createdAt: now,
        customerName: lead.primaryName,
        customerPhone: lead.primaryPhone,
        dispositionReason: data.reason,
        executionMethod: defaults.method,
        priority: defaults.priority,
        suggestedScript: defaults.script,
        sequence: defaults.sequence,
      };
      setFollowUpTasks(prev => [...prev, task]);
      toast.info("No sale recorded", { description: "Follow-up task created automatically." });

    } else if (data.outcome === "rescheduled") {
      const newDateTime = data.rescheduleDate && data.rescheduleTime
        ? `${data.rescheduleDate}T${data.rescheduleTime}:00`
        : undefined;
      if (newDateTime) {
        const assignedRepId = String(lead.assignedRep ?? "").trim();
        if (!isUuid(assignedRepId)) {
          toast.error("Assigned rep is invalid", {
            description: "Lead must be assigned to a valid rep UUID before rescheduling.",
          });
          return;
        }
        const conflictingLead = findRepAppointmentOverlap(leads, {
          rep: assignedRepId,
          candidateStartIso: newDateTime,
          excludeId: lead.id,
        });
        if (conflictingLead) {
          toast.error("Scheduling conflict", {
            description: `${lead.assignedRep || "This rep"} already has an appointment within the 2-hour appointment + 2-hour buffer window.`,
          });
          return;
        }
      }

      setLeads(prev => prev.map(l =>
        l.id === lead.id ? {
          ...l,
          stage: "Appointment Scheduled" as const,
          appointmentTime: newDateTime || l.appointmentTime,
          stageHistory: [...l.stageHistory, {
            id: crypto.randomUUID(),
            fromStage: l.stage,
            toStage: "Appointment Scheduled" as const,
            changedAt: now,
            changedBy: lead.assignedRep,
          }],
          activities: [...(l.activities || []), {
            id: crypto.randomUUID(),
            type: "note" as const,
            description: `Appointment rescheduled to ${newDateTime ? format(new Date(newDateTime), "MMM d, h:mm a") : "TBD"}. Notes: ${data.notes}`,
            createdAt: now,
            createdBy: lead.assignedRep,
          }],
          updatedAt: now,
        } : l
      ));
      toast.success("Appointment rescheduled", { description: newDateTime ? format(new Date(newDateTime), "MMM d, h:mm a") : "New date pending" });

    } else if (data.outcome === "no_demo") {
      // No build — use existing disposition flow
      const disposition: NoDemoDisposition = {
        reason: (data.reason || "not_home") as DispositionReason,
        homeownerPresent: false,
        decisionMakerPresent: false,
        mainReasonNoDemoText: data.reason || "",
        pricingDiscussed: false,
        stillLiveOpportunity: data.reason === "rescheduled",
        followUpType: data.reason === "rescheduled" ? "rep" : "none",
        followUpNotes: data.notes,
        followUpDateTime: data.rescheduleDate && data.rescheduleTime ? `${data.rescheduleDate}T${data.rescheduleTime}:00` : undefined,
        bestContactMethod: "in_person",
        completedAt: now,
        completedBy: lead.assignedRep,
      };
      handleDisposition(lead, disposition);
    }

    // Always close the modal
    setResultLead(null);
  };

  const validateRescheduleForLead = (lead: Lead, date: string, time: string): string | null => {
    const newDateTime = `${date}T${time}:00`;
    const assignedRepId = String(lead.assignedRep ?? "").trim();
    if (!isUuid(assignedRepId)) {
      return "Lead is not assigned to a valid rep UUID. Reassign the lead first.";
    }
    const conflictingLead = findRepAppointmentOverlap(leads, {
      rep: assignedRepId,
      candidateStartIso: newDateTime,
      excludeId: lead.id,
    });
    if (!conflictingLead) return null;
    return `Conflict: ${lead.assignedRep || "This rep"} is already booked for ${conflictingLead.primaryName} within the 2-hour appointment + 2-hour buffer window.`;
  };

  const handleCompleteFollowUp = (taskId: string, outcome?: "re_engaged" | "no_response" | "closed_won" | "closed_lost") => {
    setFollowUpTasks(prev => prev.map(t =>
      t.id === taskId ? { ...t, status: "completed" as const, completedAt: new Date().toISOString(), outcome: outcome || "re_engaged" } : t
    ));
    toast.success("Follow-up marked complete");
  };

  const handleSendScript = (task: FollowUpTask) => {
    if (!task.suggestedScript) return;
    const scriptText = task.suggestedScript.replace("{rep}", data.repName);
    const msg = {
      id: `msg-fu-${Date.now()}`,
      conversationId: task.opportunityId,
      direction: "outbound" as const,
      channel: "sms" as const,
      body: scriptText,
      sender: data.repName,
      timestamp: new Date().toISOString(),
      deliveryStatus: "sent" as const,
      tag: "follow-up",
    };
    setMessages(prev => [...prev, msg]);
    toast.success("Text sent", { description: `Message sent to ${task.customerName}` });
  };

  const handleLogFollowUpCall = (task: FollowUpTask) => {
    const msg = {
      id: `msg-call-${Date.now()}`,
      conversationId: task.opportunityId,
      direction: "outbound" as const,
      channel: "call" as const,
      body: `Follow-up call to ${task.customerName}`,
      sender: data.repName,
      timestamp: new Date().toISOString(),
      callDuration: 0,
      callOutcome: "spoke_to_customer" as const,
      tag: "follow-up",
    };
    setMessages(prev => [...prev, msg]);
    window.open(`tel:${task.customerPhone}`);
    toast.success("Call logged", { description: `Call initiated to ${task.customerPhone}` });
  };

  const handleCopyScript = (script: string) => {
    const text = script.replace("{rep}", data.repName);
    navigator.clipboard.writeText(text);
    toast.success("Script copied to clipboard");
  };

  const handleCompleteTouchStep = (taskId: string, touchIndex: number) => {
    setFollowUpTasks(prev => prev.map(t => {
      if (t.id !== taskId || !t.sequence) return t;
      const updated = [...t.sequence];
      updated[touchIndex] = { ...updated[touchIndex], status: "completed", completedAt: new Date().toISOString() };
      return { ...t, sequence: updated };
    }));
    toast.success("Touch step completed");
  };

  const resolvingJob = resolvingJobId ? jobsOnHold.find(h => h.job.id === resolvingJobId) : null;

  const cockpitSalesKpis: StatItem[] = [
    { label: "Appointments", value: appointments.length },
    { label: "Action Required", value: jobsOnHold.length, accent: jobsOnHold.length > 0 ? "bad" : "good" },
    { label: "Follow Ups", value: myFollowUps.length, accent: myFollowUps.length > 0 ? "warn" : undefined },
    { label: "Open Opportunities", value: data.pipeline.activeQuotes },
    { label: "Active Jobs", value: activeJobs.length },
    { label: "Pipeline Value", value: `$${(data.pipeline.pipelineValue / 1000).toFixed(0)}k` },
  ];

  return (
    <div className={isCockpit ? "space-y-6 max-w-[1600px] mx-auto w-full" : "space-y-4 max-w-[1300px] mx-auto"}>
      <RoleContextBanner />
      <RoleHandoffInbox />

      {/* ═══ HEADER ═══ */}
      {isCockpit ? (
        <CockpitHeader
          title="Sales Command Center"
          subtitle={`Appointments & pipeline · ${format(now, "EEEE, MMMM d, yyyy")}`}
        />
      ) : (
        <div>
          <h1 className="text-[22px] font-extrabold tracking-[-0.02em] text-foreground">Sales Command Center</h1>
          <p className="text-[12px] text-muted-foreground/70 mt-0.5">
            Appointments & Pipeline · {format(now, "EEEE, MMMM d, yyyy")}
          </p>
        </div>
      )}

      {/* ═══ MY SCHEDULE ═══ */}
      <MyScheduleWidget compact />

      {/* ═══ KPI ROW ═══ */}
      {isCockpit ? (
        <StatStrip items={cockpitSalesKpis} />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
          {[
            { label: "Appointments", value: appointments.length, icon: CalendarDays, color: "text-primary", bg: "bg-primary/10 border-primary/20" },
            { label: "Action Required", value: jobsOnHold.length, icon: AlertTriangle, color: jobsOnHold.length > 0 ? "text-destructive" : "text-success", bg: jobsOnHold.length > 0 ? "bg-destructive/10 border-destructive/20" : "bg-success/10 border-success/20" },
            { label: "Follow Ups", value: myFollowUps.length, icon: CalendarCheck, color: myFollowUps.length > 0 ? "text-warning" : "text-muted-foreground", bg: myFollowUps.length > 0 ? "bg-warning/10 border-warning/20" : "bg-muted/30 border-border" },
            { label: "Open Opportunities", value: data.pipeline.activeQuotes, icon: Receipt, color: "text-primary", bg: "bg-primary/10 border-primary/20" },
            { label: "Active Jobs", value: activeJobs.length, icon: Briefcase, color: "text-primary", bg: "bg-primary/10 border-primary/20" },
            { label: "Pipeline Value", value: `$${(data.pipeline.pipelineValue / 1000).toFixed(0)}k`, icon: TrendingUp, color: "text-primary", bg: "bg-primary/5 border-primary/15" },
          ].map((m) => (
            <Card key={m.label} className={`border ${m.bg} shadow-none`}>
              <CardContent className="px-2 py-3 text-center">
                <p className={`text-lg font-extrabold leading-tight ${m.color}`}>{m.value}</p>
                <p className="text-[9px] text-muted-foreground font-semibold uppercase tracking-wider leading-tight mt-1">{m.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ═══ APPOINTMENTS ═══ */}
      <section className="space-y-2.5">
        <div className="flex items-center gap-2.5">
          <CalendarDays className="h-5 w-5 text-primary" />
          <h2 className="text-[16px] font-bold tracking-tight text-foreground">Appointments</h2>
          <span className="text-[11px] font-medium text-muted-foreground">
            {appointments.length} active
          </span>
        </div>

        {appointments.length === 0 ? (
          <div className="text-center py-8 rounded-lg border border-border bg-card">
            <CalendarDays className="h-8 w-8 mx-auto text-muted-foreground/40 mb-2" />
            <p className="text-sm font-semibold text-muted-foreground">No Upcoming Appointments</p>
            <p className="text-xs text-muted-foreground mt-1">New leads will appear here when appointments are scheduled.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
             {appointments.map(({ lead, status, locked, unlockLabel, hasConsultationSession }) => {
              const statusCfg = APPT_STATUS_CONFIG[status];
              const lastActivity = lead.activities[lead.activities.length - 1];

              return (
                <Card key={lead.id} className={`border shadow-none transition-all ${locked ? "opacity-60" : "hover:-translate-y-[1px] hover:shadow-md"}`}>
                  <CardContent className="px-4 py-3.5 space-y-2.5">
                    {/* Row 1: Name + Status */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <div className={`h-2 w-2 rounded-full ${statusCfg.dot} shrink-0`} />
                        <p className="text-[14px] font-bold text-foreground leading-tight">{lead.primaryName}</p>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {locked && <Lock className="h-3 w-3 text-muted-foreground" />}
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${statusCfg.bg} ${statusCfg.color}`}>
                          {locked ? unlockLabel : statusCfg.label}
                        </span>
                      </div>
                    </div>

                    {/* Row 2: Details */}
                    <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {lead.address}, {lead.city}
                      </span>
                      <span className="flex items-center gap-1">
                        <Phone className="h-3 w-3" />
                        {lead.primaryPhone}
                      </span>
                      {lead.appointmentTime && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {format(new Date(lead.appointmentTime), "h:mm a")}
                        </span>
                      )}
                    </div>

                    {/* Row 3: Project type */}
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px] h-5">
                        {lead.projectType === "Tub to Shower Conversion" ? "Shower Conversion" : lead.projectType}
                      </Badge>
                      {lastActivity && (
                        <span className="text-[10px] text-muted-foreground truncate">
                          Last: {lastActivity.description.slice(0, 40)}...
                        </span>
                      )}
                    </div>

                    {/* Lead-source reschedule risk — muted, informational. */}
                    {(() => {
                      const risk = riskBySource.get(lead.leadSource);
                      if (!risk?.shouldShow) return null;
                      return (
                        <>
                          <p className="text-[10px] text-muted-foreground/80 leading-snug">
                            Jobs from this source often require schedule changes
                            <span className="text-muted-foreground/60"> · {risk.rate}% over last 90d (N={risk.n})</span>
                          </p>
                          <p className="text-[10px] text-muted-foreground/80 leading-snug">
                            Set flexible timeline expectations with this customer
                          </p>
                        </>
                      );
                    })()}

                    {/* Row 4: Actions */}
                    <div className="flex items-center gap-2 pt-0.5">
                      {locked ? (
                        <Button size="sm" className="h-8 text-[11px] font-bold gap-1.5" disabled>
                          <Lock className="h-3.5 w-3.5" />
                          Locked Until {format(new Date(lead.appointmentTime!), "h:mm a")}
                        </Button>
                      ) : (
                        <>
                           <Button size="sm" className="h-8 text-[11px] font-bold gap-1.5"
                             onClick={() => setActiveAppointment(lead)}>
                             <Play className="h-3.5 w-3.5" />
                             {hasConsultationSession ? "Resume Consultation" : "Start Consultation"}
                           </Button>
                          <Button size="sm" variant="outline" className="h-8 text-[11px] gap-1 border-primary/30 text-primary hover:bg-primary/5"
                            onClick={() => setResultLead(lead)}>
                            <FileText className="h-3 w-3" /> Result Appointment
                          </Button>
                      </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </section>

      {/* ═══ FOLLOW UPS ═══ */}
      {myFollowUps.length > 0 && (
        <section className="space-y-2.5">
          <div className="flex items-center gap-2.5">
            <CalendarCheck className="h-5 w-5 text-warning" />
            <h2 className="text-[16px] font-bold tracking-tight text-foreground">Follow Ups</h2>
            <span className="text-[11px] font-medium text-muted-foreground">
              {myFollowUps.length} pending
            </span>
          </div>

          <div className="space-y-2">
            {myFollowUps.map((task) => {
              const isOverdue = new Date(task.dueDate) < now;
              const isManager = task.type === "manager";
              const isExpanded = expandedFollowUp === task.id;
              const priorityCfg = task.priority ? PRIORITY_CONFIG[task.priority] : null;
              const methodLabel = task.executionMethod ? EXECUTION_METHOD_LABELS[task.executionMethod] : null;

              return (
                <Card key={task.id} className={`border shadow-none ${isOverdue ? "border-destructive/20 bg-destructive/[0.02]" : "border-warning/20"}`}>
                  <CardContent className="px-4 py-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <div className={`h-2 w-2 rounded-full ${isOverdue ? "bg-destructive" : "bg-warning"} shrink-0`} />
                        <p className="text-[14px] font-bold text-foreground">{task.customerName}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {priorityCfg && (
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${priorityCfg.bg} ${priorityCfg.color}`}>
                            {priorityCfg.label}
                          </span>
                        )}
                        <Badge variant={isManager ? "default" : "outline"} className="text-[10px] h-5">
                          {isManager ? "Manager Rehash" : "Rep Follow-Up"}
                        </Badge>
                        <span className={`text-[10px] ${isOverdue ? "text-destructive font-bold" : "text-muted-foreground"} flex items-center gap-0.5`}>
                          <Clock className="h-3 w-3" />
                          {isOverdue ? "OVERDUE" : `Due ${format(new Date(task.dueDate), "MMM d")}`}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 text-[11px] text-muted-foreground flex-wrap">
                      <span className="flex items-center gap-1">
                        <Phone className="h-3 w-3" />
                        {task.customerPhone}
                      </span>
                      {task.dispositionReason && (
                        <span>
                          Reason: <span className="font-semibold text-foreground">{task.dispositionReason}</span>
                        </span>
                      )}
                      {methodLabel && (
                        <span className="flex items-center gap-1">
                          {task.executionMethod === "call" ? <PhoneCall className="h-3 w-3" /> :
                           task.executionMethod === "text" ? <MessageCircle className="h-3 w-3" /> :
                           <MessageCircle className="h-3 w-3" />}
                          {methodLabel}
                        </span>
                      )}
                      {task.thankYouSent && (
                        <span className="text-[9px] text-success font-medium">✓ Thank-you sent</span>
                      )}
                    </div>

                    {/* Script Section */}
                    {task.suggestedScript && (
                      <div className="rounded-lg border border-border bg-muted/20 p-2.5">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Suggested Script</span>
                          <Button variant="ghost" size="sm" className="h-5 px-1.5 text-[10px] gap-1" onClick={() => handleCopyScript(task.suggestedScript!)}>
                            <Copy className="h-2.5 w-2.5" /> Copy
                          </Button>
                        </div>
                        <p className="text-[11px] text-foreground/80 leading-relaxed line-clamp-2">
                          {task.suggestedScript.replace("{rep}", data.repName)}
                        </p>
                      </div>
                    )}

                    {/* Action buttons */}
                    <div className="flex items-center gap-2 pt-0.5 flex-wrap">
                      {task.suggestedScript && (
                        <Button size="sm" className="h-8 text-[11px] font-bold gap-1.5" onClick={() => handleSendScript(task)}>
                          <Send className="h-3.5 w-3.5" /> Send Text
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="h-8 text-[11px] gap-1" onClick={() => handleLogFollowUpCall(task)}>
                        <Phone className="h-3 w-3" /> Log Call
                      </Button>
                      <Button size="sm" variant="outline" className="h-8 text-[11px] gap-1" onClick={() => handleCompleteFollowUp(task.id)}>
                        <CheckCircle className="h-3 w-3" /> Mark Complete
                      </Button>
                      {task.sequence && task.sequence.length > 1 && (
                        <Button size="sm" variant="ghost" className="h-8 text-[11px] gap-1 ml-auto"
                          onClick={() => setExpandedFollowUp(isExpanded ? null : task.id)}>
                          {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                          {task.sequence.length} Touches
                        </Button>
                      )}
                    </div>

                    {/* Multi-touch sequence (expanded) */}
                    {isExpanded && task.sequence && (
                      <div className="space-y-1.5 pt-1 border-t border-border mt-2">
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Follow-Up Sequence</p>
                        {task.sequence.map((touch, idx) => (
                          <div key={idx} className={`flex items-center gap-3 px-2.5 py-2 rounded-lg border text-[11px] ${
                            touch.status === "completed" ? "bg-success/5 border-success/20" : "border-border"
                          }`}>
                            <div className={`h-5 w-5 rounded-full flex items-center justify-center text-[9px] font-bold shrink-0 ${
                              touch.status === "completed" ? "bg-success text-success-foreground" : "bg-muted text-muted-foreground"
                            }`}>
                              {touch.status === "completed" ? "✓" : idx + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold">{touch.label}</span>
                                <span className="text-muted-foreground">Day {touch.dayOffset}</span>
                                <span className="text-[9px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                                  {EXECUTION_METHOD_LABELS[touch.method]}
                                </span>
                              </div>
                            </div>
                            {touch.status !== "completed" && (
                              <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]" onClick={() => handleCompleteTouchStep(task.id, idx)}>
                                Done
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>
      )}

      {/* ═══ NEEDS ATTENTION — COMMUNICATION ═══ */}
      {needsAttention.length > 0 && (
        <section className="space-y-2.5">
          <div className="flex items-center gap-2.5">
            <Bell className="h-5 w-5 text-warning" />
            <h2 className="text-[16px] font-bold tracking-tight text-foreground">Needs Attention</h2>
            <span className="text-[11px] font-medium text-muted-foreground">
              {needsAttention.length} new message{needsAttention.length !== 1 ? "s" : ""} from other teams
            </span>
          </div>

          <div className="space-y-2">
            {needsAttention.map(({ message, job }) => {
              const isResponding = respondingMessageId === message.id;
              const minutesAgo = Math.floor((now.getTime() - new Date(message.timestamp).getTime()) / 60000);
              const timeLabel = minutesAgo < 1 ? "Just now" : minutesAgo < 60 ? `${minutesAgo}m ago` : `${Math.floor(minutesAgo / 60)}h ago`;

              return (
                <Card key={message.id} className="border border-warning/20 shadow-none transition-all hover:-translate-y-[1px] hover:shadow-md">
                  <CardContent className="px-4 py-3.5 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <div className="h-2 w-2 rounded-full bg-warning shrink-0 animate-pulse" />
                        <p className="text-[14px] font-bold text-foreground leading-tight">
                          {job.customerName} — {job.address.city}
                        </p>
                      </div>
                      <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                        <Clock className="h-3 w-3" />
                        {timeLabel}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                        {message.context || "Internal"}
                      </span>
                      <span className="text-[11px] text-muted-foreground">
                        From: <span className="font-semibold text-foreground">{message.senderName}</span>
                      </span>
                    </div>

                    <p className="text-[12px] text-foreground/80 bg-muted/30 rounded-md px-3 py-2 line-clamp-2">
                      <Mail className="h-3 w-3 inline mr-1.5 text-muted-foreground/60" />
                      {message.body}
                    </p>

                    {!isResponding ? (
                      <div className="flex items-center gap-2 pt-0.5">
                        <Button size="sm" className="h-8 text-[11px] font-bold gap-1.5" onClick={() => setRespondingMessageId(message.id)}>
                          <MessageSquare className="h-3.5 w-3.5" /> Respond
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8 text-[11px] text-muted-foreground hover:text-foreground" onClick={() => handleDismissMessage(message.id)}>
                          Mark as Read
                        </Button>
                        <Link to={`/jobs/${job.id}`}>
                          <Button size="sm" variant="outline" className="h-8 text-[11px] gap-1">
                            <Eye className="h-3 w-3" /> View Job
                          </Button>
                        </Link>
                      </div>
                    ) : (
                      <div className="space-y-2 p-3 rounded-lg border border-primary/20 bg-primary/[0.02]">
                        <Textarea value={responseText} onChange={(e) => setResponseText(e.target.value)} placeholder="Type your response..." className="text-[12px] min-h-[60px] bg-card" autoFocus />
                        <div className="flex items-center gap-2">
                          <Button size="sm" className="h-8 text-[11px] font-bold gap-1.5" disabled={!responseText.trim()} onClick={() => handleRespondToMessage(message.id)}>
                            <Send className="h-3.5 w-3.5" /> Send Response
                          </Button>
                          <Button size="sm" variant="ghost" className="h-8 text-[11px]" onClick={() => { setRespondingMessageId(null); setResponseText(""); }}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>
      )}

      {/* ═══ ACTION REQUIRED ═══ */}
      <section className="space-y-2.5">
        <div className="flex items-center gap-2.5">
          <AlertTriangle className="h-5 w-5 text-destructive" />
          <h2 className="text-[16px] font-bold tracking-tight text-foreground">Action Required</h2>
          {jobsOnHold.length > 0 && (
            <span className="text-[11px] font-medium text-muted-foreground">
              {jobsOnHold.length} job{jobsOnHold.length !== 1 ? "s" : ""} on hold
            </span>
          )}
        </div>

        {jobsOnHold.length === 0 ? (
          <div className="text-center py-8 rounded-lg border border-border bg-card">
            <CheckCircle className="h-8 w-8 mx-auto text-success mb-2" />
            <p className="text-sm font-semibold text-success">All Clear</p>
            <p className="text-xs text-muted-foreground mt-1">No jobs require your action right now.</p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {jobsOnHold.map(({ job, revisionEvent, hoursWaiting, sla }) => {
              const slaCfg = SLA_CONFIG[sla];
              const reason = revisionEvent?.revisionReason
                ? REVISION_REASON_LABELS[revisionEvent.revisionReason]
                : "Revision Required";

              return (
                <Card key={job.id} className={`border ${slaCfg.borderColor} shadow-none transition-all hover:-translate-y-[1px] hover:shadow-md`}>
                  <CardContent className="px-4 py-3.5 space-y-2.5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <div className={`h-2 w-2 rounded-full ${slaCfg.dotColor} shrink-0`} />
                        <p className="text-[14px] font-bold text-foreground leading-tight">
                          {job.customerName} — {job.address.city}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${slaCfg.bg} ${slaCfg.color}`}>
                          {sla === "overdue" ? "⚠ OVERDUE" : sla === "warning" ? "⏰ Warning" : "Normal"}
                        </span>
                        <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                          <Clock className="h-3 w-3" />
                          {formatWaitTime(hoursWaiting)} waiting
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <span className="text-[12px] text-muted-foreground">{getProductLabel(job)}</span>
                      <span className="text-[11px] font-semibold text-destructive">{reason}</span>
                    </div>

                    {revisionEvent?.revisionNotes && (
                      <p className="text-[11px] text-foreground/70 bg-muted/30 rounded-md px-2.5 py-1.5 line-clamp-2">
                        {revisionEvent.revisionNotes}
                      </p>
                    )}

                    <div className="flex items-center gap-2 pt-0.5">
                      <Button size="sm" className="h-8 text-[11px] font-bold gap-1.5" onClick={() => setResolvingJobId(job.id)}>
                        <Wrench className="h-3.5 w-3.5" /> Fix Now
                      </Button>
                      <Link to={`/jobs/${job.id}`}>
                        <Button size="sm" variant="outline" className="h-8 text-[11px] gap-1">
                          <Eye className="h-3 w-3" /> View Job
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </section>

      {/* ═══ SECONDARY: Jobs + Quotes + Commission ═══ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 pt-2">
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardHeader className="pb-2 px-4 pt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-primary" />
                  <CardTitle className="text-sm font-semibold">My Active Jobs ({activeJobs.length})</CardTitle>
                </div>
                <Link to="/jobs" className="text-xs text-primary hover:underline inline-flex items-center gap-1">
                  All Jobs <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-1.5">
              {activeJobs.slice(0, 6).map(j => (
                <Link key={j.id} to={`/jobs/${j.id}`} className="block">
                  <div className="flex items-center justify-between p-2 rounded-lg border hover:bg-muted/30 transition-colors">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold">{j.customerName}</span>
                      <StatusBadge type="stage" value={j.currentStage} />
                    </div>
                    <span className="text-xs font-medium text-foreground">${getJobDisplayValue(j).toLocaleString()}</span>
                  </div>
                </Link>
              ))}
              {activeJobs.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-4">No active jobs</p>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2 px-4 pt-4">
              <div className="flex items-center gap-2">
                <Receipt className="h-4 w-4 text-primary" />
                <CardTitle className="text-sm font-semibold">Open Opportunities ({activeQuotes.length})</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-1.5">
              {activeQuotes.slice(0, 5).map(q => (
                <Link key={q.id} to={`/opportunities/${q.id}`} className="block">
                  <div className="flex items-center justify-between p-2 rounded-lg border hover:bg-muted/30 transition-colors">
                    <div>
                      <span className="text-xs font-semibold">{q.customerName}</span>
                      <p className="text-[10px] text-muted-foreground">{q.status}</p>
                    </div>
                    <span className="text-xs font-medium">{q.totalEstimate > 0 ? `$${q.totalEstimate.toLocaleString()}` : "—"}</span>
                  </div>
                </Link>
              ))}
              {activeQuotes.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-4">No open opportunities</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2 px-4 pt-4">
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-success" />
                <CardTitle className="text-sm font-semibold">Commission Snapshot</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-2">
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Total Closed</span>
                <span className="font-bold">${data.commissions.totalClosed.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Est. Commission</span>
                <span className="font-bold text-success">${data.commissions.estimatedCommission.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Pending</span>
                <span className="font-medium">${data.commissions.pendingCommission.toLocaleString()}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ═══ RESOLUTION WORKSPACE ═══ */}
      {resolvingJob && (
        <Sheet open={!!resolvingJobId} onOpenChange={(o) => { if (!o) { setResolvingJobId(null); setClarificationNote(""); } }}>
          <SheetContent side="right" className="w-full sm:max-w-[85vw] p-0 gap-0 [&>button]:hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-card">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-lg bg-destructive/10 flex items-center justify-center">
                  <Wrench className="h-4 w-4 text-destructive" />
                </div>
                <div>
                  <h2 className="text-[15px] font-bold text-foreground">Fix Issue — {resolvingJob.job.customerName}</h2>
                  <p className="text-[11px] text-muted-foreground">
                    {getProductLabel(resolvingJob.job)} • {resolvingJob.job.address.city}, {resolvingJob.job.address.state}
                    {" • "}
                    <span className={SLA_CONFIG[resolvingJob.sla].color}>{formatWaitTime(resolvingJob.hoursWaiting)} waiting</span>
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${SLA_CONFIG[resolvingJob.sla].bg} ${SLA_CONFIG[resolvingJob.sla].color}`}>
                  {resolvingJob.sla === "overdue" ? "⚠ OVERDUE" : resolvingJob.sla === "warning" ? "⏰ Warning" : "Normal"}
                </span>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => { setResolvingJobId(null); setClarificationNote(""); }}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="flex h-[calc(100vh-57px)]">
              <ScrollArea className="w-[65%] border-r border-border">
                <div className="p-5 space-y-5">
                  {/* Issue Summary */}
                  <section className="rounded-lg border border-destructive/20 bg-destructive/[0.03] p-4 space-y-3">
                    <h3 className="text-[14px] font-bold text-destructive flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" /> Issue Summary
                    </h3>
                    {resolvingJob.revisionEvent && (
                      <div className="space-y-2">
                        {[
                          { label: "Reason", value: resolvingJob.revisionEvent.revisionReason ? REVISION_REASON_LABELS[resolvingJob.revisionEvent.revisionReason] : "Not specified", isDestructive: true },
                          { label: "Reviewer Notes", value: resolvingJob.revisionEvent.revisionNotes },
                          { label: "Material Changes", value: resolvingJob.revisionEvent.materialChanges },
                          { label: "Flagged By", value: resolvingJob.revisionEvent.performedBy === "ops_manager" ? "Ops Reviewer" : resolvingJob.revisionEvent.performedBy },
                          { label: "Flagged At", value: format(new Date(resolvingJob.revisionEvent.timestamp), "MMM d, h:mm a") },
                        ].filter(r => r.value).map(r => (
                          <div key={r.label} className="grid grid-cols-[120px_1fr] gap-2 text-[12px]">
                            <span className="font-semibold text-muted-foreground">{r.label}</span>
                            <span className={r.isDestructive ? "font-bold text-destructive" : "text-foreground"}>{r.value}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>

                  {/* Photos */}
                  <section className="space-y-2.5">
                    <h3 className="text-[13px] font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
                      <Camera className="h-4 w-4 text-muted-foreground" /> Project Photos
                    </h3>
                    {(resolvingJob.job.photos?.length || 0) > 0 ? (
                      <div className="grid grid-cols-4 gap-2.5">
                        {resolvingJob.job.photos!.map((_, i) => (
                          <div key={i} className="aspect-[4/3] rounded-lg bg-muted/30 border border-border flex items-center justify-center">
                            <Camera className="h-5 w-5 text-muted-foreground/40" />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 p-3 rounded-lg bg-warning/5 border border-warning/20">
                        <AlertTriangle className="h-4 w-4 text-warning shrink-0" />
                        <span className="text-[12px] text-warning font-medium">No photos uploaded</span>
                      </div>
                    )}
                  </section>

                  {/* Measurements */}
                  <section className="space-y-2.5">
                    <h3 className="text-[13px] font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
                      <Ruler className="h-4 w-4 text-muted-foreground" /> Measurements
                    </h3>
                    {resolvingJob.job.projectConfig ? (
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {[
                          { label: "Total SqFt", value: resolvingJob.job.projectConfig.totalSqFt },
                          { label: "Width", value: resolvingJob.job.projectConfig.baseWidth ? `${resolvingJob.job.projectConfig.baseWidth}"` : undefined },
                          { label: "Length", value: resolvingJob.job.projectConfig.baseLength ? `${resolvingJob.job.projectConfig.baseLength}"` : undefined },
                          { label: "Base Size", value: resolvingJob.job.projectConfig.bathroomBaseSize },
                        ].filter(m => m.value).map(m => (
                          <div key={m.label} className="rounded-lg border border-border bg-card p-3">
                            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{m.label}</p>
                            <p className="text-[14px] font-bold text-foreground mt-0.5">{m.value}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[12px] text-muted-foreground">No measurements recorded</p>
                    )}
                  </section>

                  {/* Scope */}
                  <section className="space-y-2.5">
                    <h3 className="text-[13px] font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
                      <Package className="h-4 w-4 text-muted-foreground" /> Current Sold Scope
                    </h3>
                    {resolvingJob.job.projectConfig ? (
                      <div className="rounded-lg border border-border overflow-hidden">
                        {[
                          { label: "Material", value: resolvingJob.job.projectConfig.bathroomMaterials?.join(", ") },
                          { label: "Wall System", value: resolvingJob.job.projectConfig.selectedWallSystem },
                          { label: "Base", value: resolvingJob.job.projectConfig.selectedBase },
                          { label: "Glass", value: resolvingJob.job.projectConfig.selectedGlass },
                          { label: "Door", value: resolvingJob.job.projectConfig.selectedDoor },
                          { label: "Fixtures", value: resolvingJob.job.projectConfig.selectedFixture },
                          { label: "Add-ons", value: resolvingJob.job.projectConfig.selectedAddOns?.join(", ") },
                        ].filter(r => r.value).map((r, i) => (
                          <div key={r.label} className={`grid grid-cols-[140px_1fr] px-3 py-2.5 text-[12px] ${i > 0 ? "border-t border-border" : ""}`}>
                            <span className="font-semibold text-muted-foreground">{r.label}</span>
                            <span className="text-foreground">{r.value}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[12px] text-muted-foreground">No scope data available</p>
                    )}
                  </section>
                </div>
              </ScrollArea>

              <ScrollArea className="w-[35%] bg-muted/[0.02]">
                <div className="p-5 space-y-5">
                  <JobActivityTimeline
                    jobId={resolvingJob.job.id}
                    contactName={resolvingJob.job.customerName}
                    contactPhone={resolvingJob.job.phone}
                    salesRep={resolvingJob.job.salesRep || "Sales Rep"}
                    buildReviewEvents={buildReviewEvents}
                    compact
                  />

                  <section className="space-y-2.5">
                    <h3 className="text-[13px] font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
                      <Wrench className="h-4 w-4 text-muted-foreground" /> Resolution Options
                    </h3>
                    <div className="space-y-2">
                      <Link to={`/jobs/${resolvingJob.job.id}`}>
                        <Button variant="outline" className="w-full h-9 text-[12px] font-semibold gap-1.5 justify-start">
                          <Package className="h-3.5 w-3.5" /> Adjust Scope / Materials
                        </Button>
                      </Link>
                      <Link to={`/jobs/${resolvingJob.job.id}`}>
                        <Button variant="outline" className="w-full h-9 text-[12px] font-semibold gap-1.5 justify-start mt-2">
                          <FileText className="h-3.5 w-3.5" /> Trigger Change Order
                        </Button>
                      </Link>
                    </div>
                  </section>

                  <section className="space-y-2">
                    <h3 className="text-[13px] font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
                      <MessageSquare className="h-4 w-4 text-muted-foreground" /> Clarification Note
                    </h3>
                    <Textarea value={clarificationNote} onChange={(e) => setClarificationNote(e.target.value)}
                      placeholder="Add context for the reviewer about what was fixed..." className="text-[12px] min-h-[80px] bg-card" />
                  </section>

                  <section className="space-y-2.5 pt-2 border-t border-border">
                    <Button className="w-full h-10 text-[13px] font-bold gap-1.5" onClick={() => handleResubmitForReview(resolvingJob.job.id)}>
                      <Send className="h-4 w-4" /> Resubmit for Review
                    </Button>
                    <p className="text-[10px] text-muted-foreground text-center">Job will be sent back to Intake Review for re-evaluation.</p>
                  </section>
                </div>
              </ScrollArea>
            </div>
          </SheetContent>
        </Sheet>
      )}

      {/* ═══ APPOINTMENT FLOW ═══ */}
      {activeAppointment && (
        <AppointmentFlow
          lead={activeAppointment}
          open={!!activeAppointment}
          onClose={() => setActiveAppointment(null)}
        />
      )}

      {/* ═══ NO-DEMO DISPOSITION (legacy fallback) ═══ */}
      {dispositionLead && (
        <NoDemoDispositionForm
          lead={dispositionLead}
          open={!!dispositionLead}
          onClose={() => setDispositionLead(null)}
          onSubmit={(d) => handleDisposition(dispositionLead, d)}
        />
      )}

      {/* ═══ RESULT APPOINTMENT ═══ */}
      {resultLead && (
        <ResultAppointmentDialog
          lead={resultLead}
          open={!!resultLead}
          onClose={() => setResultLead(null)}
          onValidateReschedule={(date, time) => validateRescheduleForLead(resultLead, date, time)}
          onSubmit={(d) => handleResultAppointment(resultLead, d)}
        />
      )}
    </div>
  );
}
